import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import { Link, useParams } from 'react-router-dom'
import API from '../../../services/API'
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import AddNewEngagementLetterPopup from '../ClientEngagementPopup'
import LogsComponent from '../../../components/Logs/LogsComponent'
import { GlobalContext, TenantSettingsContext } from '../../../App'
import AddBox from '@mui/icons-material/AddBox'
import EmailIcon from '@mui/icons-material/Email'
import LanguageIcon from '@mui/icons-material/Language'
import PhoneIcon from '@mui/icons-material/Phone'
import ClientInformation from './ClientInformation'
import Invoices from './Invoices'
import Documents from './Documents'
import Events from './Events'
import Contacts from './Contacts'
import Loading from '../../loading/loadingPage'
import HospitalPortalAccess from './HospitalPortalAccess'
import BaseLocationRequiredDialog from './BaseLocationRequiredDialog'
import PTSContractSection from './PTSContractSection'
import PTSPerformanceSection from './PTSPerformanceSection'
import AccountManager from './AccountManager'
import { useDocumentTitle } from '../../../helpers/DocumentTitleManager'
import { vatMultiplierForItem } from '../../../helpers/region'

const getInitials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word[0].toUpperCase())
    .join('')

const getEngagementStatus = (client) => {
  if (!client.engagementExpiry)
    return { label: 'No engagement letter', color: 'default' }
  const expiry = moment(client.engagementExpiry)
  if (expiry.isBefore(moment(), 'day'))
    return {
      label: `Letter expired ${expiry.format('DD/MM/YY')}`,
      color: 'error',
    }
  if (expiry.diff(moment(), 'days') <= 60)
    return {
      label: `Letter expires ${expiry.format('DD/MM/YY')}`,
      color: 'warning',
    }
  return {
    label: `Letter valid to ${expiry.format('DD/MM/YY')}`,
    color: 'success',
  }
}

const HeroTile = ({ label, value, caption }) => (
  <Card variant="outlined" sx={{ p: 1.5 }}>
    <Typography variant="body2" color="text.secondary" noWrap>
      {label}
    </Typography>
    <Typography
      component="div"
      sx={{ fontSize: '1.1rem', fontWeight: 800, lineHeight: 1.3 }}
      noWrap
    >
      {value || '—'}
    </Typography>
    {caption && (
      <Typography variant="caption" color="text.secondary" component="div" noWrap>
        {caption}
      </Typography>
    )}
  </Card>
)

const ContactChip = ({ icon, label, href }) => (
  <Chip
    size="small"
    icon={icon}
    label={label}
    component="a"
    href={href}
    target={href.startsWith('http') ? '_blank' : undefined}
    clickable
    variant="outlined"
    sx={{ borderRadius: 1, maxWidth: 260 }}
  />
)

const Client = () => {
  const { orgConfig, tenantInfo } = useContext(TenantSettingsContext)
  const { orgSettings } = orgConfig
  const region = tenantInfo?.region
  const { id: clientID } = useParams()
  const {setPageTitle} = useDocumentTitle()
  const [client, setClient] = useState({})
  const [nextEvent, setNextEvent] = useState(null)
  const [lastEvent, setLastEvent] = useState(null)
  const [openEngagementLetter, setOpenEngagementLetter] = useState(false)
  const [loading, setLoading] = useState(true)
  const [openComment, setOpenComment] = useState(false)


  const { setToast } = useContext(GlobalContext)
  const loadEvent = () => {
    !loading && setLoading(true)
    API.get('/event-planning/organisations/' + clientID)
      .then((res) => {
        if (res.status === 200) {
          setClient(res.data.client)
          setNextEvent(res.data.nextEvent)
          setLastEvent(res.data.lastEvent)
        }
      })
      .catch((e) => console.log(e))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadEvent()
  }, [])

  useEffect(() => {
    if (client?.orgName) {
      setPageTitle(client.orgName)
    }
  }, [client?.orgName, setPageTitle])

  const totalUnpaidInvoices = client?.unpaidInvoices?.reduce(
    (accumulator, invoice) => {
      const itemsTotal = (invoice?.items || []).reduce(
        (sum, item) =>
          sum +
          (item.quantity || 0) *
            (item.unitPrice || 0) *
            vatMultiplierForItem(item.vat, region),
        0,
      )
      const paid = (invoice?.payments || []).reduce(
        (sum, payment) => sum + (payment.amount || 0),
        0,
      )
      return accumulator + (itemsTotal - paid)
    },
    0,
  )

  const currency = orgSettings?.currency ?? '£'
  const engagement = getEngagementStatus(client)
  const website =
    client.orgWebsite &&
    (client.orgWebsite.startsWith('http')
      ? client.orgWebsite
      : `https://${client.orgWebsite}`)

  return loading ? (
    <Loading />
  ) : (
    <Grid container spacing={2}>
      {client?.orgType === 'PTS' && !client?.baseLocation && (
        <BaseLocationRequiredDialog client={client} onSaved={loadEvent} />
      )}
      <Grid item xs={12}>
        <Paper sx={{ p: 2, borderRadius: 3 }}>
          <Stack spacing={2}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ xs: 'flex-start', md: 'center' }}
            >
              <Avatar
                sx={{
                  width: 64,
                  height: 64,
                  bgcolor: client.color || 'anBlue.main',
                  fontWeight: 700,
                  fontSize: '1.5rem',
                }}
              >
                {getInitials(client.orgName)}
              </Avatar>
              <Box minWidth={0} flex={1}>
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  flexWrap="wrap"
                  useFlexGap
                >
                  <Typography variant="title2">{client.orgName}</Typography>
                  {client.orgType && (
                    <Chip
                      size="small"
                      label={`${client.orgType} client`}
                      color="info"
                      sx={{ borderRadius: 1, fontWeight: 700 }}
                    />
                  )}
                  {client.orgType === 'Events' && (
                    <Chip
                      size="small"
                      label={engagement.label}
                      color={engagement.color}
                      variant={
                        engagement.color === 'default' ? 'outlined' : 'filled'
                      }
                      sx={{ borderRadius: 1, fontWeight: 700 }}
                    />
                  )}
                  {client.archived && (
                    <Chip
                      size="small"
                      label="Archived"
                      sx={{ borderRadius: 1 }}
                    />
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {[
                    client.baseLocation?.name,
                    client.orgAddressTown || client.orgAddressCity,
                  ]
                    .filter(Boolean)
                    .join(' · ')}
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.5}
                  mt={1}
                  flexWrap="wrap"
                  useFlexGap
                >
                  {client.orgEmail && (
                    <ContactChip
                      icon={<EmailIcon />}
                      label={client.orgEmail}
                      href={`mailto:${client.orgEmail}`}
                    />
                  )}
                  {client.orgTel && (
                    <ContactChip
                      icon={<PhoneIcon />}
                      label={client.orgTel}
                      href={`tel:${client.orgTel}`}
                    />
                  )}
                  {website && (
                    <ContactChip
                      icon={<LanguageIcon />}
                      label={client.orgWebsite.replace(/^https?:\/\//, '')}
                      href={website}
                    />
                  )}
                </Stack>
                <AccountManager
                  client={client}
                  onChange={(accountManager) =>
                    setClient((prev) => ({ ...prev, accountManager }))
                  }
                />
              </Box>
              {client.orgType === 'Events' && (
                <Button
                  variant="contained"
                  startIcon={<AddBox />}
                  onClick={() => setOpenEngagementLetter(true)}
                  sx={{ alignSelf: { xs: 'stretch', md: 'center' } }}
                >
                  Engagement Letter
                </Button>
              )}
            </Stack>

            {client.orgType === 'Events' && (
              <Box
                display="grid"
                gap={1.5}
                gridTemplateColumns={{
                  xs: '1fr',
                  sm: '1fr 1fr',
                  lg: 'repeat(4, 1fr)',
                }}
              >
                <HeroTile
                  label="Next Event"
                  value={
                    nextEvent?._id && (
                      <Link
                        to={`/events/${nextEvent._id}`}
                        style={{ color: 'inherit' }}
                      >
                        {moment(nextEvent.startDate).format('Do MMM YYYY')}
                      </Link>
                    )
                  }
                  caption={nextEvent?.title}
                />
                <HeroTile
                  label="Last Event"
                  value={
                    lastEvent?._id && (
                      <Link
                        to={`/events/${lastEvent._id}`}
                        style={{ color: 'inherit' }}
                      >
                        {moment(lastEvent.startDate).format('Do MMM YYYY')}
                      </Link>
                    )
                  }
                  caption={lastEvent?.title}
                />
                <HeroTile
                  label="Invoices Due"
                  value={
                    totalUnpaidInvoices > 0
                      ? `${currency}${Math.round(
                          totalUnpaidInvoices,
                        ).toLocaleString()}`
                      : null
                  }
                  caption={
                    client?.unpaidInvoices?.length > 0
                      ? `${client.unpaidInvoices.length} invoice${
                          client.unpaidInvoices.length === 1 ? '' : 's'
                        } awaiting payment`
                      : 'Nothing outstanding'
                  }
                />
                <HeroTile
                  label="Engagement Letter Expires"
                  value={
                    client.engagementExpiry
                      ? moment(client.engagementExpiry).format('Do MMM YYYY')
                      : null
                  }
                  caption={
                    client?.engagementLetters?.length > 0
                      ? `${client.engagementLetters.length} letter${
                          client.engagementLetters.length === 1 ? '' : 's'
                        } on file`
                      : 'None on file'
                  }
                />
              </Box>
            )}
          </Stack>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <ClientInformation client={client} />
      </Grid>
      {client?.orgType === 'PTS' && (
        <Grid item xs={12}>
          <PTSContractSection client={client} />
        </Grid>
      )}
      {client?.orgType === 'PTS' && (
        <Grid item xs={12}>
          <PTSPerformanceSection client={client} />
        </Grid>
      )}
      {client?.orgType === 'PTS' && (
        <Grid item xs={12}>
          <HospitalPortalAccess client={client} />
        </Grid>
      )}
      <Grid item xs={12}>
        <Contacts client={client} loadEvent={loadEvent} />
      </Grid>
      {client?.orgType === 'Events' && (
        <Grid item xs={12}>
          <Events clientID={clientID} />
        </Grid>
      )}
      <Grid item xs={12}>
        <Invoices clientID={clientID} orgType={client?.orgType} />
      </Grid>
      <Grid item xs={12}>
        <Documents client={client} />
      </Grid>
      <Grid item xs={12}>
        <Box component={Paper} borderRadius={1} padding={2}>
          <LogsComponent
            targetType="organisation"
            targetId={client._id}
            sx={{
              borderRadius: 1,
              background: null,
              boxShadow: null,
            }}
            openComment={openComment}
            setOpenComment={setOpenComment}
            allowCreateLog
            title="Note"
          />
        </Box>
      </Grid>
      <AddNewEngagementLetterPopup
        open={openEngagementLetter}
        onClose={() => {
          setOpenEngagementLetter(false)
          loadEvent()
        }}
        clientID={client._id}
        clientName={client.orgName}
      />
    </Grid>
  )
}

export default Client
